import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, Check, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY_BENEFITS = /cancel|interrupt|delay|missed connection|medical|baggage/i;
const LIVE_HOSTS = ['airaecho.com', 'www.airaecho.com'];

const CancellationNotice = () => (
  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100 space-y-1" data-testid="insurance-cancellation-notice">
    <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />How cancellation cover works</p>
    <p>• <b>Passenger cancels voluntarily</b> (change of plans): the insurer's wording does not state this is covered.</p>
    <p>• <b>Passenger cancels for an unforeseen event</b>: covered only for the reasons listed in the insurer's Terms &amp; Conditions, up to the limit and after the deductible.</p>
    <p>• <b>Airline cancels the flight</b>: the plan text refers to the insurer's Terms &amp; Conditions; airline refunds or rebooking are handled by the airline, not guaranteed by this plan.</p>
    <p>• <b>Delays / missed connections</b>: separate benefits with their own limits and time deductibles (shown below).</p>
    <p className="text-amber-200/80">No payout is guaranteed. Claims are assessed by the insurer.</p>
  </div>
);

const Plan = ({ plan, chosen, onChoose }) => (
  <div className={`rounded-xl border p-3 ${chosen ? 'border-[#E6006E] bg-[#E6006E]/10' : 'border-white/10 bg-white/[0.03]'}`} data-testid={`insurance-plan-${plan.pid}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-white">{plan.tier} {plan.name}</p>
        <p className="text-xs text-white/50">Provider: {plan.provider} · {plan.region_name}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-white" data-testid={`insurance-premium-${plan.pid}`}>₹{Number(plan.pricing?.total_fare || 0).toLocaleString('en-IN')}</p>
        <p className="text-[10px] text-white/45">premium, all travellers</p>
      </div>
    </div>
    <ul className="mt-2 space-y-1">
      {(plan.benefits || []).filter((b) => b.type === 'INSURANCE_BENEFIT' && b.sumins && KEY_BENEFITS.test(b.name)).map((b) => (
        <li key={b.name} className="text-xs text-white/70"><span className="text-white">{b.name}</span> — {b.sumins}. <span className="text-white/50">“{b.dsc}”</span></li>
      ))}
    </ul>
    <button onClick={() => onChoose(chosen ? null : plan)} data-testid={`insurance-choose-${plan.pid}`}
      className={`mt-2 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${chosen ? 'bg-[#E6006E] text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}>
      {chosen ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" />Selected — tap to remove</span> : 'Add this plan'}
    </button>
  </div>
);

export const FlightInsuranceOffer = ({ flight, travellers = 1 }) => {
  const [plans, setPlans] = useState(null);
  const [env, setEnv] = useState(null);
  const [choice, setChoice] = useState(undefined);

  useEffect(() => {
    (async () => {
      try {
        const { data: st } = await axios.get(`${API}/api/insurance/status`);
        const liveHost = LIVE_HOSTS.includes(window.location.hostname);
        if (!st.enabled || (liveHost && st.environment !== 'PRODUCTION_VERIFIED')) return setPlans([]);
        setEnv((st.base_url || '').includes('apitest') ? 'UAT' : st.environment);
        let cc = flight?.destinationCountry;
        if (!cc && flight?.destination) {
          const { data } = await axios.get(`${API}/api/tripjack/airports`, { params: { query: flight.destination } });
          cc = (data.airports || []).find((a) => a.code === flight.destination)?.country_code;
        }
        if (!cc || cc === 'IN') return setPlans([]);
        const start = (flight.departureTime || '').slice(0, 10);
        const end = new Date(new Date(start).getTime() + 7 * 864e5).toISOString().slice(0, 10);
        const { data } = await axios.post(`${API}/api/insurance/search`, {
          start_date: start, end_date: end, regions: [{ rkey: cc, rt: 'COUNTRY' }], travelers: Array.from({ length: travellers }, () => ({ age: 30 })) });
        setPlans((data.plans || []).slice(0, 3));
      } catch {
        setPlans([]);
      }
    })();
  }, [flight, travellers]);

  const record = async (plan) => {
    setChoice(plan);
    try {
      await axios.post(`${API}/api/insurance/offer-choice`, { flight_price_id: flight?.priceId, choice: plan ? 'accepted' : 'declined', pid: plan?.pid, plid: plan?.plid, premium: plan?.pricing?.total_fare });
    } catch { /* choice still shown locally */ }
  };

  if (plans === null) return <div className="flex items-center gap-2 text-xs text-white/50 mb-4"><Loader2 className="w-3.5 h-3.5 animate-spin" />Checking travel insurance options…</div>;
  if (!plans.length) return null;
  return (
    <section className="mb-6 rounded-2xl border border-white/10 bg-[#14101F] p-4 space-y-3" data-testid="flight-insurance-offer">
      <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#FF4FA3]" /><h3 className="font-semibold text-white">Optional travel insurance</h3>
        {env !== 'PRODUCTION' && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] text-amber-200" data-testid="insurance-test-badge">TEST DATA · preview only</span>}</div>
      <p className="text-xs text-white/60">Not included in your flight price. Choose a plan or skip — nothing is preselected. Premiums shown are quotes for {travellers} traveller(s) aged 30 on a 7-day trip; the final premium depends on actual ages and dates.</p>
      <CancellationNotice />
      {plans.map((p) => <Plan key={p.pid} plan={p} chosen={choice?.pid === p.pid} onChoose={record} />)}
      <button onClick={() => record(null)} data-testid="insurance-decline-btn" className={`text-xs underline ${choice === null ? 'text-white' : 'text-white/50'}`}>{choice === null ? 'You chose no insurance' : 'No thanks, continue without insurance'}</button>
      <div className="rounded-xl bg-white/[0.04] p-3 text-[11px] text-white/55 space-y-1" data-testid="insurance-missing-info">
        <p>Full policy wording, exclusions, eligibility rules, claim procedure and insurer claim-support contacts are <b>not provided by our insurance API yet</b>. They will be linked here before insurance can be purchased.</p>
        {choice && <p className="text-amber-200" data-testid="insurance-not-charged-note">Your choice is recorded. Insurance purchase is not yet enabled, so no premium will be charged and no policy is issued with this booking.</p>}
      </div>
    </section>
  );
};
